import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.clearway-mobility.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

// Reused inside every page-level JSON-LD node so each indexable URL carries a
// publisher and a link back to the site entity, not just a bare title.
const PUBLISHER = {
  '@type': 'Organization',
  name: 'ClearWay Mobility',
  legalName: 'Clearway Mobility LLP',
  url: SITE_URL,
  logo: `${SITE_URL}/logo-512.png`
};

const WEBSITE = {
  '@type': 'WebSite',
  name: 'ClearWay Mobility',
  url: SITE_URL
};

// schemaType picks the WebPage subtype for the route. Anything omitted falls
// back to a plain WebPage, which is still valid and still gives the page a
// name/description/publisher/breadcrumb triple that AI answer engines can cite.
const SEO_BY_PATH = {
  '/': {
    title: 'ClearWay | Smart Cane with Obstacle Detection for the Visually Impaired',
    description:
      'ClearWay is a smart cane (smart mobility stick) with forward obstacle detection, surface sensing, and Apple Find My support for visually impaired and elderly users.',
    keywords:
      'smart cane, smart mobility stick, smart walking stick, visually impaired mobility aid, blind assistance device, assistive technology',
    type: 'website'
  },
  '/features': {
    title: 'Smart Cane Features | Obstacle & Surface Detection | ClearWay',
    description:
      'Explore ClearWay smart cane features including forward obstacle detection, terrain scanning, smart alerts, and practical accessibility-focused design.',
    keywords: 'smart cane features, obstacle detection cane, mobility stick sensors, accessibility features',
    breadcrumbLabel: 'Features'
  },
  '/technology': {
    title: 'How the ClearWay Smart Cane Works | Sensor Technology',
    description:
      'Learn how the ClearWay smart cane combines ultrasonic and infrared sensor intelligence, ergonomic design, and reliability for safer everyday mobility.',
    keywords: 'smart cane technology, smart cane sensors, assistive mobility technology, accessibility innovation',
    breadcrumbLabel: 'Technology'
  },
  '/find-my': {
    title: 'Apple Find My Integration | Locate Your ClearWay Smart Cane',
    description:
      'ClearWay supports Apple Find My so users and caregivers can help locate the smart cane quickly and confidently.',
    keywords: 'apple find my mobility aid, gps tracker for blind person, locate smart cane, clearway tracking',
    breadcrumbLabel: 'Find My'
  },
  '/about': {
    title: 'About ClearWay | Mission and Team',
    description:
      'Discover the mission behind ClearWay and the team focused on practical mobility support for visually impaired and elderly users.',
    keywords: 'about clearway, assistive technology mission, mobility support team',
    schemaType: 'AboutPage',
    breadcrumbLabel: 'About'
  },
  '/impact': {
    title: 'Impact | Real-World Accessibility Outcomes with ClearWay',
    description:
      'See how ClearWay contributes to safer navigation, greater confidence, and improved independence across daily journeys.',
    keywords: 'accessibility impact, mobility independence, assistive technology outcomes',
    breadcrumbLabel: 'Impact'
  },
  '/press': {
    title: 'Press & Media Coverage | ClearWay in the News',
    description:
      'ClearWay press coverage: the smart cane launch was carried by 160 outlets including The Hindu, India Today, Yahoo Finance, Business Standard, PTI and ANI, with independent features from ElectronicsForU and others.',
    keywords: 'clearway press, clearway news, smart cane news coverage, clearway mobility media, assistive technology press',
    schemaType: 'CollectionPage',
    breadcrumbLabel: 'Press'
  },

  '/blog': {
    title: 'ClearWay Blog | Mobility Insights & Updates',
    description:
      'Read ClearWay blog updates, mobility safety insights, and accessibility stories from the team.',
    keywords: 'clearway blog, mobility insights, accessibility stories, assistive technology updates',
    type: 'website',
    schemaType: 'CollectionPage',
    breadcrumbLabel: 'Blog'
  },

  '/partner': {
    title: 'Partner with ClearWay | Accessibility and Distribution Partners',
    description:
      'Partner with ClearWay to expand access to smart mobility support for communities, organizations, and institutions.',
    keywords: 'clearway partnership, assistive technology distribution, accessibility partner',
    breadcrumbLabel: 'Partner'
  },
  '/shop': {
    title: 'Buy the ClearWay Smart Cane | Price & Shipping (India & UAE)',
    description:
      'Buy the ClearWay smart cane (smart walking stick) — see price, configuration options, 30-day returns, and shipping to India and the UAE.',
    keywords: 'buy smart cane, smart cane price india, buy smart walking stick, clearway price, mobility aid purchase',
    type: 'product',
    schemaType: 'ItemPage',
    breadcrumbLabel: 'Shop'
    // The page-level ItemPage node here sits alongside the Product+Offer
    // JSON-LD that ShopPage injects from live product data, so the structured
    // price always matches the visible one.
  },
  '/configure': {
    title: 'Configure Your ClearWay Smart Cane | Build & Price',
    description:
      'Build your ClearWay smart cane in six steps — white cane, walking stick, or ClearWay fitted to the stick you already own, plus height and hand fit, detection range, and Apple Find My. See your price, then an advisor confirms the build before you pay.',
    keywords:
      'configure smart cane, custom white cane, smart cane builder, cane length by height, clearway configurator, made to order mobility aid, fit clearway to my own cane, smart cane retrofit',
    type: 'product',
    schemaType: 'ItemPage',
    // References the single Product node declared on /shop rather than
    // declaring a second one, so the two pages describe one product.
    mainEntityId: `${SITE_URL}/shop#product`,
    breadcrumbLabel: 'Configure'
  },
  '/contact': {
    title: 'Contact ClearWay | Support and Enquiries',
    description:
      'Get in touch with ClearWay for customer support, product enquiries, partnership requests, and accessibility questions.',
    keywords: 'clearway contact, assistive device support, mobility aid help',
    schemaType: 'ContactPage',
    breadcrumbLabel: 'Contact'
  },
  '/warranty': {
    title: 'Warranty & Support | ClearWay Mobility',
    description:
      'Review ClearWay warranty terms and support information for your smart mobility stick.',
    keywords: 'clearway warranty, mobility stick support, assistive product coverage',
    breadcrumbLabel: 'Warranty'
  },
  '/support': {
    title: 'Support | ClearWay Mobility',
    description: 'Get product support resources and warranty guidance for your ClearWay device.',
    keywords: 'clearway support, smart cane help, product support',
    // /support renders the same component as /warranty — canonicalize there so
    // the two URLs don't compete as duplicates.
    canonical: '/warranty',
    breadcrumbLabel: 'Warranty'
  },
  '/safety-notice': {
    title: 'Safety Notice | ClearWay Mobility',
    description:
      'Read important safety guidance for using ClearWay effectively as an assistive mobility support tool.',
    keywords: 'clearway safety notice, mobility stick safety, assistive device guidance',
    breadcrumbLabel: 'Safety Notice'
  },
  '/training': {
    title: 'Training | Using ClearWay Effectively',
    description:
      'Access training guidance to use ClearWay confidently, including best practices for safer navigation.',
    keywords: 'clearway training, smart mobility aid usage, accessibility training',
    breadcrumbLabel: 'Training'
  },
  '/terms': {
    title: 'Terms and Conditions | ClearWay Mobility',
    description: 'Read the ClearWay terms and conditions for use, purchase, and service policies.',
    keywords: 'clearway terms, terms and conditions, purchase policy',
    breadcrumbLabel: 'Terms and Conditions'
  },
  '/privacy': {
    title: 'Privacy Policy | ClearWay Mobility',
    description:
      'Read the ClearWay privacy policy and understand how we handle user information and communications.',
    keywords: 'clearway privacy policy, data policy, user privacy',
    breadcrumbLabel: 'Privacy Policy'
  },
  '/refund': {
    title: 'Refund Policy | ClearWay Mobility',
    description: 'Review ClearWay refund policy details for eligible purchases and return workflows.',
    keywords: 'clearway refund policy, return policy, mobility stick return',
    breadcrumbLabel: 'Refund Policy'
  },
  '/checkout': {
    title: 'Checkout | ClearWay Mobility',
    description: 'Secure checkout for your ClearWay order.',
    keywords: 'checkout',
    robots: 'noindex, nofollow'
  },
  '/order/success': {
    title: 'Order Confirmed | ClearWay Mobility',
    description: 'Your ClearWay order has been confirmed.',
    keywords: 'order success',
    robots: 'noindex, nofollow'
  },
  '/order/failed': {
    title: 'Order Failed | ClearWay Mobility',
    description: 'There was a problem processing your ClearWay order.',
    keywords: 'order failed',
    robots: 'noindex, nofollow'
  },
  '/track': {
    title: 'Track Order | ClearWay Mobility',
    description: 'Track your ClearWay order status.',
    keywords: 'track order',
    robots: 'noindex, nofollow'
  },
  '/support/device-check': {
    title: 'Device Check | ClearWay Mobility',
    description: 'Run a diagnostic check on your ClearWay device.',
    keywords: 'device check, clearway diagnostics',
    robots: 'noindex, nofollow'
  },
  '/admin': {
    title: 'Admin | ClearWay Mobility',
    description: 'Administrative access for ClearWay.',
    keywords: 'admin',
    robots: 'noindex, nofollow'
  }
};

// Served for any path not in SEO_BY_PATH: noindex so soft-404s (the SPA
// fallback returns 200 for unknown URLs) never enter the index, and no JSON-LD
// so unknown URLs don't emit Product schema.
const NOT_FOUND_CONFIG = {
  title: 'Page Not Found | ClearWay Mobility',
  description: 'The page you are looking for does not exist.',
  keywords: 'ClearWay Mobility',
  robots: 'noindex, nofollow'
};

const ensureMeta = (selector, createFn) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = createFn();
    document.head.appendChild(el);
  }
  return el;
};

const SEOManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

    if (normalizedPath.startsWith('/blog/')) {
      return;
    }

    const isDynamicRoute =
      normalizedPath.startsWith('/order/') ||
      normalizedPath.startsWith('/configure/') ||
      normalizedPath.startsWith('/pay/') ||
      normalizedPath.startsWith('/admin/') ||
      normalizedPath.startsWith('/support/');
    const config =
      SEO_BY_PATH[normalizedPath] ||
      (isDynamicRoute ? { ...NOT_FOUND_CONFIG, title: 'ClearWay Mobility' } : NOT_FOUND_CONFIG);
    const canonicalPath = config.canonical || normalizedPath;
    const canonicalUrl = `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath}`;
    const robots = config.robots || 'index, follow';

    document.title = config.title;

    const description = ensureMeta('meta[name="description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      return meta;
    });
    description.setAttribute('content', config.description);

    const keywords = ensureMeta('meta[name="keywords"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'keywords');
      return meta;
    });
    keywords.setAttribute('content', config.keywords || 'ClearWay Mobility');

    const robotsMeta = ensureMeta('meta[name="robots"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      return meta;
    });
    robotsMeta.setAttribute('content', robots);

    const canonical = ensureMeta('link[rel="canonical"]', () => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      return link;
    });
    canonical.setAttribute('href', canonicalUrl);

    const ogTitle = ensureMeta('meta[property="og:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      return meta;
    });
    ogTitle.setAttribute('content', config.title);

    const ogDescription = ensureMeta('meta[property="og:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      return meta;
    });
    ogDescription.setAttribute('content', config.description);

    const ogUrl = ensureMeta('meta[property="og:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      return meta;
    });
    ogUrl.setAttribute('content', canonicalUrl);

    const ogType = ensureMeta('meta[property="og:type"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      return meta;
    });
    ogType.setAttribute('content', config.type || 'website');

    const ogImage = ensureMeta('meta[property="og:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image');
      return meta;
    });
    ogImage.setAttribute('content', DEFAULT_IMAGE);

    const ogImageAlt = ensureMeta('meta[property="og:image:alt"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image:alt');
      return meta;
    });
    ogImageAlt.setAttribute('content', 'The ClearWay smart cane, a white mobility cane fitted with an obstacle-detection sensor unit');

    const twitterTitle = ensureMeta('meta[property="twitter:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'twitter:title');
      return meta;
    });
    twitterTitle.setAttribute('content', config.title);

    const twitterDescription = ensureMeta('meta[property="twitter:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'twitter:description');
      return meta;
    });
    twitterDescription.setAttribute('content', config.description);

    const twitterUrl = ensureMeta('meta[property="twitter:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'twitter:url');
      return meta;
    });
    twitterUrl.setAttribute('content', canonicalUrl);

    let jsonLd = document.getElementById('route-jsonld');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'route-jsonld';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }

    // Every indexable route gets a page-level node so no URL in the sitemap
    // ships with a bare title and nothing machine-readable behind it. Noindex
    // routes get none — there is nothing to describe to a crawler that is not
    // supposed to keep the URL.
    if (config.jsonLd) {
      jsonLd.textContent = JSON.stringify({
        ...config.jsonLd,
        url: canonicalUrl
      });
    } else if (robots.startsWith('index')) {
      jsonLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': config.schemaType || 'WebPage',
        '@id': canonicalUrl,
        url: canonicalUrl,
        name: config.title,
        description: config.description,
        inLanguage: 'en',
        isPartOf: WEBSITE,
        publisher: PUBLISHER,
        primaryImageOfPage: { '@type': 'ImageObject', url: DEFAULT_IMAGE },
        ...(config.mainEntityId ? { mainEntity: { '@id': config.mainEntityId } } : {})
      });
    } else {
      jsonLd.textContent = '';
    }

    // BreadcrumbList: Home > [page] for every indexable top-level route.
    // Blog posts build their own three-level trail in BlogPostPage.
    let breadcrumbEl = document.getElementById('breadcrumb-jsonld');
    if (!breadcrumbEl) {
      breadcrumbEl = document.createElement('script');
      breadcrumbEl.id = 'breadcrumb-jsonld';
      breadcrumbEl.type = 'application/ld+json';
      document.head.appendChild(breadcrumbEl);
    }
    if (config.breadcrumbLabel && robots.startsWith('index')) {
      breadcrumbEl.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: config.breadcrumbLabel, item: canonicalUrl }
        ]
      });
    } else {
      breadcrumbEl.textContent = '';
    }
  }, [pathname]);

  return null;
};

export default SEOManager;
